/**
 * Translation service using Google Translate API
 * This provides on-demand translation of backend/admin panel content
 */

// Cache for translated texts to avoid redundant API calls
const translationCache = {
  hi: {},
  ur: {}
};

/**
 * Translate text to the target language using Google Translate API
 * 
 * @param {string} text - Text to translate
 * @param {string} targetLang - Target language code (e.g., 'hi' for Hindi)
 * @returns {Promise<string>} - Translated text
 */
export const translateText = async (text, targetLang) => {
  // Return original text if not provided or if target language is English
  if (!text || targetLang === 'en') {
    return text;
  }

  // Return cached translation if available
  if (translationCache[targetLang]?.[text]) {
    return translationCache[targetLang][text];
  }

  try {

    // Using a free translation endpoint (replace with your preferred service if needed)
    const encodedText = encodeURIComponent(text);
    const url = `https://translate.googleapis.com/translate_a/single?client=gtx&sl=en&tl=${targetLang}&dt=t&q=${encodedText}`;
    
    const response = await fetch(url);
    
    if (!response.ok) {
      console.error(`Translation API responded with status: ${response.status}`);
      return text; // Return original text on non-200 responses
    }
    
    const data = await response.json();
    
    // Verify we have valid data structure
    if (!data || !Array.isArray(data[0])) {
      console.error('Unexpected translation API response structure:', data);
      return text;
    }
    
    // Extract translated text from response
    const translatedText = data[0].map(item => item[0]).join('');
    
    
    // Cache the translation result
    if (!translationCache[targetLang]) {
      translationCache[targetLang] = {};
    }
    translationCache[targetLang][text] = translatedText;
    
    return translatedText;
  } catch (error) {
    console.error('Translation error:', error);
    return text; // Return original text on error
  }
};

/**
 * Translate an object's text properties
 * 
 * @param {Object} obj - Object with text properties
 * @param {string} targetLang - Target language code
 * @returns {Promise<Object>} - Object with translated properties
 */
export const translateObject = async (obj, targetLang) => {
  // Return original object if not provided or if target language is English
  if (!obj || targetLang === 'en') {
    return obj;
  }

  const translatedObj = { ...obj };
  const promises = [];
  const keys = [];
  const skipKeys = ['image', 'img', 'icon', 'logo', 'imageUrl', 'imgUrl', 'imageSrc', 'imgSrc', 'logoSrc', 'logoUrl'];

  // Process each property in the object
  for (const key in translatedObj) {
    if (skipKeys.includes(key)) {
      continue;
    }
    
    if (typeof translatedObj[key] === 'string') {
      // Add translation promise for string properties
      keys.push(key);
      promises.push(translateText(translatedObj[key], targetLang));
    } else if (typeof translatedObj[key] === 'object' && translatedObj[key] !== null) {
      // Recursively translate nested objects
      translatedObj[key] = await translateObject(translatedObj[key], targetLang);
    }
  }

  // Wait for all translations to complete
  const translatedValues = await Promise.all(promises);
  
  // Update object with translated values
  for (let i = 0; i < keys.length; i++) {
    translatedObj[keys[i]] = translatedValues[i];
  }

  return translatedObj;
};

/**
 * Translate an array of objects
 * 
 * @param {Array} array - Array of objects to translate
 * @param {string} targetLang - Target language code
 * @returns {Promise<Array>} - Array with translated objects
 */
export const translateArray = async (array, targetLang) => {
  if (!array || !Array.isArray(array) || targetLang === 'en') {
    return array;
  }

  const translatedArray = [];
  for (const item of array) {
    if (typeof item === 'object' && item !== null) {
      translatedArray.push(await translateObject(item, targetLang));
    } else if (typeof item === 'string') {
      translatedArray.push(await translateText(item, targetLang));
    } else {
      translatedArray.push(item);
    }
  }

  return translatedArray;
};
